import React, { MouseEvent } from 'react';
import styled from 'styled-components/macro';
import Dropdown, { IOption, PopoverPlacement } from 'shared/components/Dropdown';
import { VerticalMenuIcon } from 'assets/icons';
import { FormContent } from 'shared/types/forms';
import { useFormExport, usePdfDownload } from './hooks';
import { ClinicPet as ClinicPet, SpeciesType } from 'shared/types/graphql';
import { useTracking } from 'shared/providers/TrackingProvider';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';
import { FormExportModal } from './FormExportModal';
import { useDisclosure } from '@televet/kibble-ui/build/chakra';

enum ExportOptions {
  DownloadPDF = 'DownloadPDF',
  ChoosePets = 'ChoosePets',
  ExportToPims = 'ExportToPims',
}

export interface Form {
  id: string;
  title: string;
  content: FormContent;
  pet?: ClinicPet | null;
}

export interface ExportModalPet {
  id: string;
  name?: string;
  pimsId?: string | null;
  species?: SpeciesType;
  assigned?: boolean;
}

export interface ExportModalPetParent {
  id: string;
  name: string;
}

interface FormExportMenuProps {
  form: Form;
  pets: ExportModalPet[];
  petParent: ExportModalPetParent;
}

export const FormExportMenu = ({ form, pets, petParent }: FormExportMenuProps): JSX.Element => {
  const { trackEvent } = useTracking();
  const toast = useToast();
  const { isOpen, onClose, onOpen: openFormExport } = useDisclosure();

  // Setup Form Export
  const {
    available: formExportAvailable,
    integrationName,
    exportFormToPims: triggerPimsExport,
  } = useFormExport({
    form,
  });

  // Setup PDF Download
  const downloadPdf = usePdfDownload(form.content, form.id, form.pet);

  // Setup Menu - Clean this up
  const exportOptions = [{ text: 'Download as PDF', value: ExportOptions.DownloadPDF }] as IOption[];

  const handleExportClick = (e: MouseEvent, option: IOption): void => {
    if (option.value === ExportOptions.DownloadPDF) {
      trackEvent(ConversationEvents.FormDownloadPdf);
      downloadPdf();
      toast({ title: 'Generating PDF...', status: 'info', id: 'pdf-generate' });
    } else if (option.value === ExportOptions.ExportToPims) {
      const pet = pets.find((pet) => pet.pimsId != null && pet.id === form?.pet?.id) || pets[0];
      if (pet) {
        trackEvent(ConversationEvents.FormExportToPims, { pets: pet.id });
        triggerPimsExport([{ id: pet.id, name: pet.name || '' }]);
      } else {
        trackEvent(ConversationEvents.FormExportToPims, { petId: 'none' });
      }
      toast({
        title: `Exporting to ${integrationName}`,
        status: 'info',
        id: 'pims-export',
      });
    } else if (option.value === ExportOptions.ChoosePets) {
      trackEvent(ConversationEvents.FormExportToPimsMultiPet);
      openFormExport();
    }
  };

  if (formExportAvailable) {
    if (pets.filter((p) => !!p.pimsId).length > 0) {
      exportOptions.unshift({
        text: `Select patients to export to ${integrationName}`,
        value: ExportOptions.ChoosePets,
      });
    } else {
      exportOptions.push({
        text: `No patients to export to ${integrationName}`,
        disabledMessage: `No patients to export to ${integrationName}`,
        isDisabled: true,
        value: ExportOptions.ChoosePets,
      });
    }
    // Defer to one on the form
    const pet = pets.find((pet) => pet.pimsId != null && pet.id === form?.pet?.id) || pets[0];
    if (pet) {
      if (pet.pimsId) {
        exportOptions.unshift({
          text: `Export form for ${pet.name} to ${integrationName}`,
          disabledMessage: `${pet.name} is not synced with ${integrationName}`,
          value: ExportOptions.ExportToPims,
        });
      } else {
        exportOptions.push({
          text: `${pet.name} not synced with ${integrationName}`,
          value: '',
          isDisabled: true,
          disabledMessage: `${pet.name} is not synced with ${integrationName}`,
        });
      }
    }
  }

  return (
    <>
      <Dropdown
        options={exportOptions}
        onSelect={handleExportClick}
        placement={PopoverPlacement.BottomStart}
        width={300}
      >
        <ExportOptionsButton
          onClick={(e): void => {
            e.stopPropagation();
          }}
        >
          <VerticalMenuIcon />
        </ExportOptionsButton>
      </Dropdown>
      <FormExportModal onClose={onClose} isOpen={isOpen} form={form} pets={pets || []} petParentName={petParent.name} />
    </>
  );
};

const ExportOptionsButton = styled.button`
  border: none;
  background: transparent;
  z-index: 0;
  width: 22px;
  height: 22px;
  border-radius: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #eee;
  }
`;
